import type { WidgetPublico } from '../api/ops/misReservas';

/**
 * La marca de las páginas del negocio (G614, G618, ADR-0173): en la web, `brand.primary_color` y tema claro; en el
 * widget, `theme` y `accent_color` (la dirección de 13.8 puede mandar `tema` y `acento`). El texto sobre el acento es
 * blanco o tinta según el contraste (WCAG, 4,5 a 1 con el blanco).
 */
export type Tema = 'claro' | 'oscuro';

const HEX = /^#?([0-9a-f]{6})$/i;
const TINTA = '#111111';

function luminancia(hex: string): number | null {
    const m = HEX.exec(hex.trim());
    if (m === null) {
        return null;
    }
    const v = m[1] ?? '000000';
    const canal = (i: number) => {
        const c = parseInt(v.slice(i, i + 2), 16) / 255;
        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * canal(0) + 0.7152 * canal(2) + 0.0722 * canal(4);
}

/** Blanco si contrasta lo bastante con el color; si no, tinta. */
export function tintaSobre(color: string): string {
    const l = luminancia(color);
    if (l === null) {
        return '#ffffff';
    }
    return 1.05 / (l + 0.05) >= 4.5 ? '#ffffff' : TINTA;
}

/** Tema y acento del widget: `?tema=claro|oscuro|auto` y `?acento=RRGGBB` mandan sobre lo guardado. */
export function marcaDelWidget(w: Pick<WidgetPublico, 'theme' | 'accent_color'>, busqueda: URLSearchParams, oscuroDelSistema = typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches): { tema: Tema; acento: string } {
    const pedido = busqueda.get('tema') ?? (w.theme === 'dark' ? 'oscuro' : w.theme === 'light' ? 'claro' : w.theme === 'auto' ? 'auto' : 'claro');
    const tema: Tema = pedido === 'oscuro' || (pedido === 'auto' && oscuroDelSistema) ? 'oscuro' : 'claro';
    const acento = busqueda.get('acento');
    const color = acento !== null && HEX.test(acento) ? `#${acento.replace('#', '')}` : w.accent_color ?? TINTA;
    return { tema, acento: color };
}
